import { ref } from "vue";

const STORAGE_PREFIX = "lifeworth-encounter-message-unread";
const MAX_ENCOUNTERS = 100;
const MAX_MESSAGES_PER_ENCOUNTER = 50;
const stateCache = new Map();

export const encounterMessageUnreadRevision = ref(0);

const numericId = value => Number(value?.id ?? value ?? 0);
const readRevision = () => encounterMessageUnreadRevision.value;

const userStorageKey = user => {
  if (!user?.id || !user?.type) return null;

  const providerId = user.type === "provider"
    ? numericId(user.provider_id || user.account)
    : 0;

  return `${STORAGE_PREFIX}:${user.type}:${user.id}:${providerId}`;
};

const loadState = user => {
  const key = userStorageKey(user);
  if (!key) return { key: null, state: {} };

  if (stateCache.has(key)) {
    return { key, state: stateCache.get(key) };
  }

  let state = {};

  try {
    const stored = window.localStorage.getItem(key);
    const parsed = stored ? JSON.parse(stored) : {};
    state = parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    state = {};
  }

  stateCache.set(key, state);
  return { key, state };
};

const saveState = (key, state) => {
  if (!key) return;

  stateCache.set(key, state);

  try {
    if (Object.keys(state).length) {
      window.localStorage.setItem(key, JSON.stringify(state));
    } else {
      window.localStorage.removeItem(key);
    }
  } catch {
    // The realtime workflow remains usable when browser storage is unavailable.
  }

  encounterMessageUnreadRevision.value += 1;
};

export const trackUnreadEncounterMessage = (user, event) => {
  const encounterId = String(event?.encounter_id || "");
  const messageId = String(event?.message_id || "");

  if (!encounterId || !messageId) return false;

  const { key, state } = loadState(user);
  if (!key) return false;

  const existing = Array.isArray(state[encounterId]) ? state[encounterId] : [];

  if (existing.includes(messageId)) return false;

  const nextState = {
    ...state,
    [encounterId]: [...existing, messageId].slice(-MAX_MESSAGES_PER_ENCOUNTER),
  };
  const encounterIds = Object.keys(nextState);

  if (encounterIds.length > MAX_ENCOUNTERS) {
    encounterIds
      .slice(0, encounterIds.length - MAX_ENCOUNTERS)
      .forEach(id => delete nextState[id]);
  }

  saveState(key, nextState);
  return true;
};

export const unreadEncounterMessageIds = (user, encounterId) => {
  readRevision();

  const id = String(encounterId || "");
  if (!id) return [];

  const { state } = loadState(user);
  return Array.isArray(state[id]) ? [...state[id]] : [];
};

export const unreadEncounterMessageCount = (user, encounterId) => {
  return unreadEncounterMessageIds(user, encounterId).length;
};

export const markEncounterMessagesRead = (user, encounterId, messageIds = []) => {
  const id = String(encounterId || "");
  if (!id) return false;

  const { key, state } = loadState(user);
  const existing = Array.isArray(state[id]) ? state[id] : [];
  if (!key || !existing.length) return false;

  const readIds = new Set((messageIds || []).map(String));
  const remaining = readIds.size
    ? existing.filter(messageId => !readIds.has(String(messageId)))
    : [];

  if (remaining.length === existing.length) return false;

  const nextState = { ...state };

  if (remaining.length) {
    nextState[id] = remaining;
  } else {
    delete nextState[id];
  }

  saveState(key, nextState);
  return true;
};
